import React, { useMemo } from 'react'
import type {
  StampsShelfItem,
  ShelfHorizontalPosition,
  ShelfVerticalPosition
} from 'src/typings/stamps'
import useWindowDimensions from 'src/hooks/useWindowDimensions'

import './stamps-shelf.scss'

interface StampsShelfProps {
  stampVerticalPosition: '1' | '2' | '3'
  allItems: StampsShelfItem[] | undefined
  collections: CollectionsType
}

const StampsShelf: React.FC<StampsShelfProps> = ({
  stampVerticalPosition,
  allItems,
  collections,
}) => {
  const baseClass = 'product-stamp'
  const { isMobile } = useWindowDimensions()

  const calculatePosition = (
    shelfHorizontalPosition: ShelfHorizontalPosition,
    shelfVerticalPosition: ShelfVerticalPosition
  ) => {
    const styles: React.CSSProperties = {
      top: '4px',
      bottom: 'auto',
      left: 'auto',
      right: 'auto',
      transform: 'none',
    }
  
    if (shelfHorizontalPosition === '1') {
      styles.left = '0px'
    } else if (shelfHorizontalPosition === '2') {
      styles.left = '50%'
      styles.transform = 'translateX(-50%)'
    } else if (shelfHorizontalPosition === '3') {
      if (shelfVerticalPosition === '1') {
        styles.right = isMobile ? '0px' : '32px'
      } else {
        styles.right = '0px'
      }
    }
  
    return styles
  }  

  const filteredItems = useMemo(() => {
    if (!allItems || !collections) {
      return []
    }
  
    const validItems = allItems.filter((item) =>
      item.collections.some((collection) =>
        collections.some((col) => col.id === collection.collectionID)
      )
    )
  
    const usedVerticalPositions = new Set<string>()
    const filtered: StampsShelfItem[] = []
  
    for (const item of validItems) {
      const firstValidCollection = item.collections.find((collection) => {
        const settings = collection.collectionShelfSettings
  
        const verticalPosition = settings?.collectionShelfVerticalPosition
  
        const isSamePosition = verticalPosition === stampVerticalPosition
  
        if (
          collection.collectionShowShelf &&
          settings &&
          isSamePosition &&
          !usedVerticalPositions.has(verticalPosition)
        ) {
          return true
        }
  
        return false
      })
  
      if (firstValidCollection) {
        const verticalPosition = firstValidCollection.collectionShelfSettings?.collectionShelfVerticalPosition
        usedVerticalPositions.add(verticalPosition!)
        filtered.push({
          ...item,
          collections: [firstValidCollection],
        })
      }
    }
  
    return filtered
  }, [allItems, collections, stampVerticalPosition])

  const containerHeight = useMemo(() => {
    if (!allItems || !collections) {
      return 0
    }
  
    let maxHeight = 0
  
    for (const item of allItems) {
      for (const collection of item.collections) {
        const settings = collection.collectionShelfSettings
  
        const isVisible = isMobile
          ? settings?.collectionShelfMobileVisibility
          : settings?.collectionShelfDesktopVisibility
  
        if (!collection.collectionShowShelf || !settings || !isVisible) {
          continue
        }
  
        const height = isMobile
          ? parseInt(settings.collectionShelfMobileHeight || '0', 10)
          : parseInt(settings.collectionShelfDesktopHeight || '0', 10)
  
        if (height > maxHeight) {
          maxHeight = height
        }
      }
    }
  
    return maxHeight
  }, [allItems, collections, isMobile])  

  return (
    <>
      <div
        className="stamps-container"
        style={{ height: `${containerHeight}px` }}
      >
        {filteredItems.length > 0 && filteredItems.map((item, index) => {
          const collection = item.collections[0]
          const settings = collection.collectionShelfSettings

          if (!collection.collectionShowShelf || !settings) {
            return null
          }

          const {
            collectionShelfHorizontalPosition,
            collectionShelfVerticalPosition,
            collectionShelfDesktopVisibility,
            collectionShelfMobileVisibility,
            collectionShelfDesktopFontSize,
            collectionShelfMobileFontSize,
            collectionShelfDesktopWidth,
            collectionShelfDesktopHeight,
            collectionShelfMobileWidth,
            collectionShelfMobileHeight,
          } = settings

          const isVisible = isMobile
            ? collectionShelfMobileVisibility
            : collectionShelfDesktopVisibility

          if (!isVisible) {
            return null
          }

          const styles = calculatePosition(collectionShelfHorizontalPosition, collectionShelfVerticalPosition)

          return (
            <div key={index} className="stamps-wrapper">
              <div
                data-collection={collection.collectionID}
                className={baseClass}
                style={{
                  ...styles,
                  position: 'absolute',
                  fontSize: isMobile
                    ? `${collectionShelfMobileFontSize}px`
                    : `${collectionShelfDesktopFontSize}px`,
                  fontWeight: item.fontWeight || '500',
                  letterSpacing: `${item.fontLetterSpacing || '0'}px`,
                  color: item.fontColor,
                  backgroundColor: item.backgroundColor,
                  width: isMobile
                    ? `${collectionShelfMobileWidth}px`
                    : `${collectionShelfDesktopWidth}px`,
                  height: isMobile
                    ? `${collectionShelfMobileHeight}px`
                    : `${collectionShelfDesktopHeight}px`,
                  borderRadius: `${item.borderRadius}px`,
                  border:
                    item.borderSize && item.borderColor
                      ? `${item.borderSize}px solid ${item.borderColor}`
                      : 'none',
                }}
              >
                {item.link ? (
                  <a
                    href={item.link}
                    rel="noopener"
                    style={{ color: item.fontColor }}
                  >
                    {item.label}
                  </a>
                ) : (
                  <span>{item.label}</span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}

export default StampsShelf
